import './styles.scss'
import React, { useContext, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
/* commented for future reference */
// import { Avatar } from 'primereact/avatar'
import { FileUpload } from 'primereact/fileupload'
import { InputText } from 'primereact/inputtext'
import CustomButton from 'Components/UIComponent/Button'
import DialogUIModal from 'Components/UIComponent/DialogUIModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { supabase } from 'Services/supabaseClient'

const CreatePostModal = ({ visible, closeModal, organizationStatus, mediaList, onOrganizationStatusChange, onPost }) => {
  const { t } = useTranslation()
  const { setLoading } = useContext(CoffeeWebContext)
  const userDetails = getLocalUserDetails()
  const placeHolder = process.env.REACT_APP_DESCRIPTION_INPUT_PLACEHOLDER
  // Ref for media FileUpload component so we can clear the internal file input after selection
  const mediaFileUploadRef = useRef(null)
  //  `organizationStatus === 0` indicates that the organization has not been created
  //  `organizationStatus === 2` indicates that the created organization is pending approval

  const [localOrgStatus, setLocalOrgStatus] = useState(organizationStatus)
  const [media, setMedia] = useState([])
  const [formData, setFormData] = useState({
    text: '',
    link: ''
  })

  const [orgForm, setOrgForm] = useState({
    organisationName: '',
    profileImage: null,
    profilePreview: null
  })

  useEffect(() => {
    setLocalOrgStatus(organizationStatus)
  }, [organizationStatus])

  useEffect(() => {
    if (!visible) {
      setMedia([])
      setFormData({
        text: '',
        link: ''
      })
      if (orgForm.profilePreview) {
        URL.revokeObjectURL(orgForm.profilePreview)
      }

      setOrgForm({
        organisationName: '',
        profileImage: null,
        profilePreview: null
      })
    }
  }, [visible])

  const submitPost = async () => {
    if (!formData.text && !media.length && !formData.link) return

    setLoading(true)

    try {
      const uploadedMedia = await uploadAllMediaToSupabase()

      const payload = {
        channelLogo: '',
        userid: userDetails?.id,
        description: formData.text,
        channelName: '',
        country: userDetails?.countryId.toString(),
        channelLink: formData.link,
        media: uploadedMedia
      }

      const response = await apiAdapterCoffeeWeb.createPost(payload)

      onPost({
        ...response?.data
      })

      setFormData({ text: '', link: '' })
      setMedia([])

      closeModal()
    } catch (err) {
      console.error('Error submitting post:', err)
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const uploadToSupabaseStorage = async (file, folderName = 'coffee_posts') => {
    const BUCKET_NAME = 'coffeeweb_social'
    const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200 MB (Supabase bucket limit)
    const CHUNK_SIZE = 50 * 1024 * 1024 // 50 MB chunks for large files (Supabase handles this internally, but we can track progress)
    const MAX_RETRIES = 3
    const RETRY_DELAY = 2000

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File exceeds maximum size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
    }

    if (file.size === 0) {
      throw new Error('File is empty')
    }

    // Generate file path with folder structure
    const sanitizeFileName = (name) =>
      name
        .replace(/[^\w\s\-.]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .trim()

    const safeFolderName = sanitizeFileName((folderName || 'coffee_posts').trim())
    const timestamp = Date.now()
    const sanitizedFileName = sanitizeFileName(file.name)
    const uniqueFileName = `${timestamp}_${sanitizedFileName}`
    const filePath = safeFolderName ? `${safeFolderName}/${uniqueFileName}` : uniqueFileName

    // Determine if file should be chunked (files larger than chunk size)
    const shouldChunk = file.size > CHUNK_SIZE

    // Upload function with retry logic
    const uploadAttempt = async () => {
      let uploadResult

      if (shouldChunk) {
        // Chunked upload for large files using Supabase's efficient upload method
        // Supabase Storage handles chunking internally, but we can implement explicit chunking for better control
        uploadResult = await uploadFileInChunks(file, filePath, BUCKET_NAME, CHUNK_SIZE)
      } else {
        // Direct upload for small files
        const { data, error } = await supabase.storage.from(BUCKET_NAME).upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'application/octet-stream'
        })

        if (error) {
          if (error.message.includes('duplicate') || error.message.includes('already exists')) {
            const { data: retryData, error: retryError } = await supabase.storage.from(BUCKET_NAME).upload(filePath, file, {
              cacheControl: '3600',
              upsert: true,
              contentType: file.type || 'application/octet-stream'
            })

            if (retryError) throw retryError
            uploadResult = retryData
          } else {
            throw error
          }
        } else {
          uploadResult = data
        }
      }

      // Get public URL
      const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath)
      const publicUrl = urlData.publicUrl

      return {
        url: publicUrl,
        media_url: publicUrl,
        s3_key: filePath,
        s3_bucket: BUCKET_NAME,
        name: file.name,
        mime: file.type,
        size: file.size
      }
    }

    // Retry logic with Promise and recursion
    const attemptUpload = async (attemptsLeft) => {
      try {
        return await uploadAttempt()
      } catch (error) {
        if (attemptsLeft <= 1) {
          throw error
        }

        console.warn(`⚠️ Upload attempt failed, ${attemptsLeft - 1} retries remaining:`, error)

        // Create delay promise
        const delay = () =>
          new Promise((resolve) => {
            setTimeout(resolve, RETRY_DELAY)
          })

        // Wait for delay then recursively try again
        await delay()

        return attemptUpload(attemptsLeft - 1)
      }
    }

    // Start recursive retry chain
    try {
      return await attemptUpload(MAX_RETRIES)
    } catch (error) {
      throw error || new Error('Upload failed after maximum retry attempts')
    }
  }

  const uploadFileInChunks = async (file, filePath, bucketName, chunkSize) => {
    const { data, error } = await supabase.storage.from(bucketName).upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'application/octet-stream'
    })

    if (error) {
      // Retry with upsert if duplicate
      if (error.message.includes('duplicate') || error.message.includes('already exists')) {
        const { data: retryData, error: retryError } = await supabase.storage.from(bucketName).upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type || 'application/octet-stream'
        })

        if (retryError) throw retryError

        return retryData
      }
      throw error
    }

    return data
  }

  const uploadAllMediaToSupabase = async () => {
    try {
      const uploaded = await Promise.all(
        media.map(async (item, index) => {
          try {
            const uploadResult = await uploadToSupabaseStorage(item.file, `user_${userDetails?.id || 'anonymous'}`)

            if (!uploadResult?.media_url) return null

            const mediaObject = {
              mediatype: item.file.type || uploadResult.mime,
              medianame: item.file.name || uploadResult.name,
              mediaurl: uploadResult.media_url || uploadResult.url,
              mediaSequence: index + 1
            }

            return mediaObject
          } catch (error) {
            console.error(`Error uploading file ${item.file.name}:`, error)
            throw error
          }
        })
      )

      return uploaded.filter(Boolean)
    } catch (error) {
      console.error('Error uploading media to Supabase:', error)
      throw error
    }
  }

  const submitOrganization = async () => {
    if (!orgForm.organisationName || !orgForm.profileImage) return

    setLoading(true)

    try {
      const formData = new FormData()

      formData.append('file', orgForm.profileImage)

      const imageResponse = await apiAdapterCoffeeWeb.uploadFileToS3Bucket(formData)

      const payload = {
        organisationName: orgForm.organisationName,
        userId: userDetails?.id,
        profileImage: imageResponse?.data
      }

      const response = await apiAdapterCoffeeWeb.createOrganization(payload)

      if (response?.data?.isVerified === false) {
        setLocalOrgStatus(2) // 2 = Pending approval
        onOrganizationStatusChange(2)
      }
    } catch (err) {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const handleOrgChange = (e) => {
    const { name, value } = e.target

    setOrgForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleOrgImageSelect = (e) => {
    if (!e.files?.length) return

    const file = e.files[0]
    const previewUrl = URL.createObjectURL(file)

    setOrgForm((prev) => ({
      ...prev,
      profileImage: file,
      profilePreview: previewUrl
    }))
  }

  const handleRemoveOrgImage = () => {
    if (orgForm?.profilePreview) {
      URL.revokeObjectURL(orgForm?.profilePreview)
    }

    setOrgForm((prev) => ({
      ...prev,
      profileImage: null,
      profilePreview: null
    }))
  }

  const onMediaSelect = (e) => {
    if (!e.files?.length) return

    const selectedFiles = e.files.map((file, index) => ({
      file,
      type: file.type.startsWith('image') ? 'image' : 'video',
      preview: URL.createObjectURL(file),
      sequence: media.length + index + 1
    }))

    setMedia((prev) => [...prev, ...selectedFiles])

    // Clear internal input so clicking Add Media again opens the chooser
    if (mediaFileUploadRef?.current?.clear) {
      mediaFileUploadRef.current.clear()
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const renderCreateOrganizationForm = () => (
    <div className="create-org-container">
      <div>
        <label htmlFor="organisationName" className="input-label-text">
          {t('ORGANIZATION_NAME')}
          <span className="required-field">*</span>
        </label>

        <InputText id="organisationName" name="organisationName" value={orgForm?.organisationName} onChange={handleOrgChange} placeholder={t('ENTER_ORGANIZATION_NAME')} className="create-post-input" />
      </div>

      {!orgForm?.profilePreview && <FileUpload mode="basic" accept="image/*" chooseLabel={t('UPLOAD_PROFILE_IMAGE')} customUpload auto onSelect={handleOrgImageSelect} className="thumbnail-upload" />}

      {orgForm?.profilePreview && (
        <div className="org-image-preview">
          <img src={orgForm?.profilePreview} alt="Organization Profile" />
          <Button icon="pi pi-times" className="remove-preview-btn" onClick={handleRemoveOrgImage} />
        </div>
      )}

      <p className="note">
        <strong>{t('NOTE_TITLE')}</strong> {t('NOTE_BODY')}
        <br />
        <br />
        {t('NOTE_FOOTER')}
      </p>
    </div>
  )

  const renderPendingApproval = () => (
    <div className="pending-approval">
      <h3 className="modal-title">Thank you for your submission.</h3>
      <p className="modal-message">{t('ORG_PENDING_MESSAGE')}</p>
    </div>
  )

  const renderCreatePostModalContent = () => (
    <div>
      <div className="create-post-header">
        {/* commented for future reference */}
        {/* <Avatar image={mediaList?.profileImage} size="large" shape="circle" className="profile-avatar" /> */}
        {/* <span>{mediaList?.oranisationName}</span> */}
        <span>{t('POST_CONTENT')}</span>
      </div>

      <div className="create-post-body">
        <InputTextarea name="text" value={formData.text} onChange={handleChange} placeholder={placeHolder} rows={5} className="create-post-input text-area" />
        <InputText name="link" value={formData.link} onChange={handleChange} placeholder={t('ADD_A_WEBSITE_LINK')} className="create-post-input link-input" />

        {media?.length > 0 && (
          <div className={`image-grid grid-${media?.length}`}>
            {media?.map((item, index) => (
              <div className="image-wrapper" key={index}>
                {item.type === 'image' ? <img src={item?.preview} alt={`media-${index}`} /> : <video src={item?.preview} controls controlsList="nodownload noplaybackrate" disablePictureInPicture disableRemotePlayback />}

                <Button icon="pi pi-times" className="remove-img" onClick={() => setMedia(media.filter((_, i) => i !== index))} />
              </div>
            ))}
          </div>
        )}

        <FileUpload ref={mediaFileUploadRef} mode="basic" accept="image/*,video/*" multiple chooseLabel={t('ADD_MEDIA')} customUpload auto={true} onSelect={onMediaSelect} className="thumbnail-upload" />
      </div>
    </div>
  )

  const renderCreatePostFooterActions = () => {
    if (localOrgStatus === 0) {
      return (
        <div className="create-post-footer-actions">
          <CustomButton buttonType="CUSTOM" buttonLabel={t('SUBMIT')} variant="hard-action" onButtonClick={submitOrganization} disabled={!orgForm?.organisationName || !orgForm?.profileImage} />
        </div>
      )
    }

    if (localOrgStatus === 2) {
      return null
    }

    return (
      <div className="create-post-footer-actions">
        <CustomButton buttonType={'CUSTOM'} buttonLabel={t('POST_MEDIA')} variant={'hard-action'} onButtonClick={submitPost} disabled={media?.length === 0} />
      </div>
    )
  }

  const getModalHeading = (status) => {
    if (status === 0) return t('CREATE_ORGANIZATION')
    if (status === 2) return ''

    return t('CREATE_POST')
  }

  const getModalClassname = (status) => {
    if (status === 0) return 'create-organization-dialog'
    if (status === 2) return 'pending-approval-dialog'

    return 'post-creation-dialog'
  }

  const bodyRenderer = () => {
    if (localOrgStatus === 0) {
      return renderCreateOrganizationForm()
    }

    if (localOrgStatus === 2) {
      return renderPendingApproval()
    }

    return renderCreatePostModalContent()
  }

  return (
    <>
      <DialogUIModal size="md" title={getModalHeading(organizationStatus)} showModal={visible} handleClose={closeModal} bodyContent={bodyRenderer} footerContent={renderCreatePostFooterActions} customClassName={`coffee-post-modal ${getModalClassname(organizationStatus)} `} />
    </>
  )
}

export default CreatePostModal
